import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useShiftData } from "../../hooks/shiftData";
import axios from "../../api/axios";
import { destroySession } from "../../helpers/Session";
import toast from "../sweetalert/Sweetalert";

const Daily = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { shiftData } = useShiftData();

    const timeoutRef = useRef(null);
    const accesstoken = sessionStorage.getItem("accesstoken");
    const uname = sessionStorage.getItem("uname");
    const role = sessionStorage.getItem("role");

    const [formSubmit, setFormSubmit] = useState(false);
    const [selectedShifts, setSelectedShifts] = useState({});
    const [changedRows, setChangedRows] = useState(new Set());
    const [disableAll, setDisableAll] = useState(false);

    const dailyShiftKey = ["dailyshift", shiftData];

    const {
        data: dailyshift,
        isLoading: isLoadingDailyShift,
        error: dailyShiftError,
    } = useQuery({
        queryKey: dailyShiftKey,
        queryFn: async () => {
            const res = await axios.get(
                `shifts/employees?payperiod=${shiftData?.payperiod}&uname=${shiftData?.employee}`,
                { headers: { Authorization: `Bearer ${accesstoken}` } }
            );
            return res.data.response;
        },
        enabled: !!(shiftData?.payperiod && shiftData?.employee),
    });

    useEffect(() => {
        if (dailyShiftError?.response?.data.status === "inactive") {
            destroySession();
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                navigate("/error");
            }, 1000);
        }
    }, [dailyShiftError]);
    
    useEffect(() => {
        if (dailyshift?.schedules) {
            const initialShifts = {};
            dailyshift.schedules.forEach((item) => {
                initialShifts[item.id] = item.shift || "";
            });
            setSelectedShifts(initialShifts);
            // keep disableAll state unchanged after refresh
        }
    }, [dailyshift]);

    const handleShiftChange = (id, value) => {
        setSelectedShifts((prev) => ({ ...prev, [id]: value }));
        setChangedRows((prev) => new Set(prev).add(id));
        setDisableAll(false);
    };

    const dailyShiftMutation = useMutation({
        mutationFn: async (dailyShiftData) => {
            const res = await axios.post("shifts/employees", dailyShiftData, {
                headers: { Authorization: `Bearer ${accesstoken}` },
            });
            return res.data.response;
        },
        onError: (error) => {
            setFormSubmit(false);
            const errorMsg =
                error.response?.data?.response ||
                "Something went wrong while saving employee daily shifts!";
            toast.fire({ icon: "error", title: errorMsg });

            if (error.response?.data?.status === "inactive") {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => {
                    navigate("/error");
                }, 1000);
            }
        },
        onSuccess: async (response) => {
            setFormSubmit(false);
            setDisableAll(true);
            if (role === "staff") {
                toast.fire({
                    icon: "info",
                    title: "Shift schedule submitted for approval",
                });
            } else {
                toast.fire({ icon: "success", title: response });
            }

            // refresh from server
            await queryClient.invalidateQueries(dailyShiftKey);
        },
    });
    
    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append("payperiod", shiftData?.payperiod);
        formData.append("uname", shiftData?.employee);
        formData.append("creator", uname);
        formData.append("source", "daily");

        dailyshift.schedules.forEach((item) => {
            formData.append("date[]", item.rawdate);
            formData.append("department[]", item.department);
            formData.append("shift[]", selectedShifts[item.id] || "");

            if (role === "staff") {
                formData.append(
                    "status[]",
                    changedRows.has(item.id) ? "0" : item.status
                );
            } else {
                formData.append("status[]", item.status);
            }
        });

        setFormSubmit(true);
        dailyShiftMutation.mutate(formData);
    };  


    const hasPending = dailyshift?.schedules?.some((s) => s.status === 0);

    return (
        <div className="daily-schedule">
            <form onSubmit={handleSubmit}>
                <div className="panel">
                    <div className="panel-title">
                        <h3>Daily Schedule</h3>
                        <button type="submit" disabled={formSubmit || hasPending}>
                            {formSubmit ? (
                                <span>
                                    SAVING <i className="fa fa-spinner fa-spin"></i>
                                </span>
                            ) : (
                                "SAVE"
                            )}
                        </button>
                    </div>

                    <div className="panel-content">
                        <table className={isLoadingDailyShift ? 'loading' : ''}>
                            <thead>
                                <tr>
                                    <th>Day</th>
                                    <th>Shift</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoadingDailyShift
                                    ? [...Array(10)].map((_, i) => (
                                          <tr key={i}>
                                              <td>
                                                  <span></span>
                                              </td>
                                              <td>
                                                  <span></span>
                                              </td>
                                          </tr>
                                      ))
                                    : dailyshift?.schedules?.map((item) => {
                                          const leaveForDay = dailyshift?.leaves?.find(
                                              (leave) => {
                                                  const day = new Date(item.rawdate);
                                                  const start = new Date(leave.start_date);
                                                  const end = new Date(leave.end_date);
                                                  return day >= start && day <= end;
                                              }
                                          );

                                          const isOnLeave = leaveForDay?.leave_name || false;
                                          const isPending = item.status === 0;

                                          return (
                                              <tr key={item.id}>
                                                  <td>
                                                      <span>{item.date}</span>
                                                  </td>
                                                  <td>
                                                      <span>
                                                          <input
                                                              type="hidden"
                                                              name="date[]"
                                                              value={item.rawdate}
                                                          />
                                                          <input
                                                              type="hidden"
                                                              name="department[]"
                                                              value={item.department}
                                                          />

                                                          {isOnLeave ? (
                                                              <>
                                                                  <input
                                                                      type="hidden"
                                                                      name="shift[]"
                                                                      value={
                                                                          selectedShifts[item.id] ||
                                                                          ""
                                                                      }
                                                                  />
                                                                  <span
                                                                      style={{
                                                                          display: "block",
                                                                          textAlign: "center",
                                                                          fontWeight: "bold",
                                                                          color: "#643e0d",
                                                                          backgroundColor:
                                                                              "#fff5e9",
                                                                          borderRadius: "4px",
                                                                          padding: "6px",
                                                                          cursor: "not-allowed",
                                                                          userSelect: "none",
                                                                          width: "100%",
                                                                      }}
                                                                  >
                                                                      {isOnLeave}
                                                                  </span>
                                                              </>
                                                          ) : (
                                                              <select
                                                                  name="shift[]"
                                                                  value={
                                                                      selectedShifts[item.id] || ""
                                                                  }
                                                                  onChange={(e) =>
                                                                      handleShiftChange(
                                                                          item.id,
                                                                          e.target.value
                                                                      )
                                                                  }
                                                                  disabled={
                                                                      disableAll || isPending
                                                                  }
                                                                  title={
                                                                      disableAll
                                                                          ? "Disabled after save"
                                                                          : isPending
                                                                          ? "Pending for approval"
                                                                          : ""
                                                                  }
                                                                  style={{
                                                                      backgroundColor:
                                                                          changedRows.has(item.id)
                                                                              ? "#fff5e9"
                                                                              : isPending
                                                                              ? "#fff5e9"
                                                                              : "white",
                                                                      fontWeight:
                                                                          changedRows.has(item.id)
                                                                              ? "bold"
                                                                              : "normal",
                                                                  }}
                                                              >
                                                                  <option value=""></option>
                                                                  {dailyshift?.shifts?.map(
                                                                      (shift) => (
                                                                          <option
                                                                              key={shift.id}
                                                                              value={shift.shift}
                                                                          >
                                                                              {shift.shift}
                                                                          </option>
                                                                      )
                                                                  )}
                                                              </select>
                                                          )}
                                                      </span>
                                                  </td>
                                              </tr>
                                          );
                                      })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </form>
        </div>
    )
}

export default Daily;